import { ConnectorType, } from '../types/connector';
import { ParameterError } from '../types/errors';
const accessToken = (placeholder) => ({
    key: 'accessToken',
    label: 'Access token',
    placeholder,
    secret: true,
    required: true,
});
const textField = (key, label, placeholder) => ({
    key,
    label,
    placeholder,
    secret: false,
    required: true,
});
/** `textField`'s counterpart for keys that must render as a password input. */
const secretField = (key, label, placeholder) => ({
    key,
    label,
    placeholder,
    secret: true,
    required: true,
});
const requireString = (source, key) => {
    const value = source[key];
    if (typeof value !== 'string' || value.length === 0) {
        throw new ParameterError(`Source credentials are missing '${key}' for this connector`);
    }
    return value;
};
// Google issues a refresh token only for access_type=offline on a consented grant.
const googleOAuth = (scopes) => ({
    tokenUrl: 'https://oauth2.googleapis.com/token',
    authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    scopes,
    usePkce: true,
    authorizeParams: { access_type: 'offline', prompt: 'consent' },
    extraParams: {},
});
const googleCursor = {
    type: 'cursor',
    cursor_path: 'nextPageToken',
    cursor_param: 'pageToken',
};
// Drive file listing of one Google editor type, the parent of a per-file content call.
const googleDriveListing = (name, mimeType) => ({
    name,
    endpoint: {
        path: 'files',
        params: {
            pageSize: 1000,
            q: `mimeType='${mimeType}' and trashed=false`,
            fields: 'nextPageToken,files(id,name,modifiedTime,createdTime,owners)',
        },
        data_selector: 'files',
    },
});
/**
 * A Record rather than a switch on purpose: a new `ConnectorType` member fails
 * to compile until its entry is declared here, so adding connector #4..#70 is
 * appending one object and nothing else.
 *
 * `${key}` strings inside a source config are resolved against the source
 * credentials inside the dlt runner, so no secret is ever in here.
 */
export const CONNECTOR_MANIFEST = {
    [ConnectorType.GITHUB]: {
        displayName: 'GitHub',
        sourceFields: [
            accessToken('ghp_...'),
            {
                key: 'repository',
                label: 'Repository',
                placeholder: 'owner/repo',
                secret: false,
                required: true,
            },
        ],
        oauth: null,
        // GitHub issue body. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'body',
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://api.github.com/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // GitHub paginates with RFC-5988 Link headers.
                paginator: { type: 'header_link' },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'issues',
                    endpoint: {
                        path: `repos/${requireString(source, 'repository')}/issues`,
                        params: {
                            state: 'all',
                            per_page: 100,
                            // Ascending, so an interrupted sync resumes
                            // from the oldest unseen record.
                            sort: 'updated',
                            direction: 'asc',
                            since: {
                                type: 'incremental',
                                cursor_path: 'updated_at',
                                initial_value: '2020-01-01T00:00:00Z',
                            },
                        },
                    },
                },
            ],
        }),
    },
    [ConnectorType.HUBSPOT_DEALS]: {
        displayName: 'HubSpot Deals',
        sourceFields: [accessToken('pat-na1-...')],
        oauth: {
            tokenUrl: 'https://api.hubapi.com/oauth/v1/token',
            authorizeUrl: 'https://app.hubspot.com/oauth/authorize',
            scopes: ['crm.objects.deals.read', 'crm.objects.owners.read'],
            // HubSpot documents no PKCE support for its authorization code
            // grant, and an unexpected code_challenge is rejected.
            usePkce: false,
            authorizeParams: {},
            extraParams: {},
        },
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.hubapi.com/crm/v3/',
                auth: { type: 'bearer', token: '${accessToken}' },
                paginator: {
                    type: 'json_link',
                    next_url_path: 'paging.next.link',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'deals',
                    endpoint: {
                        path: 'objects/deals',
                        // Naming properties replaces HubSpot's defaults,
                        // so the defaults are listed alongside the owner.
                        params: {
                            limit: 100,
                            properties: 'dealname,amount,dealstage,pipeline,closedate,createdate,hs_lastmodifieddate,hubspot_owner_id',
                        },
                        data_selector: 'results',
                    },
                },
                {
                    // Who owns each deal, matched to a person by email.
                    // Replaced each sync so a removed owner drops out.
                    name: 'owners',
                    write_disposition: 'replace',
                    endpoint: {
                        path: 'owners',
                        params: { limit: 100 },
                        data_selector: 'results',
                        // A token without the owners scope must not fail
                        // the sync; mapped people then see no deals.
                        response_actions: [
                            { status_code: 401, action: 'ignore' },
                            { status_code: 403, action: 'ignore' },
                        ],
                    },
                },
            ],
        }),
    },
    [ConnectorType.ZENDESK]: {
        displayName: 'Zendesk',
        sourceFields: [
            accessToken('Zendesk API token'),
            {
                key: 'subdomain',
                label: 'Subdomain',
                placeholder: 'acme',
                secret: false,
                required: true,
            },
        ],
        oauth: {
            tokenUrl: 'https://${subdomain}.zendesk.com/oauth/tokens',
            authorizeUrl: 'https://${subdomain}.zendesk.com/oauth/authorizations/new',
            scopes: ['read'],
            usePkce: true,
            authorizeParams: {},
            extraParams: {},
        },
        // Zendesk ticket's first comment. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'description',
        buildSourceConfig: (source) => ({
            client: {
                base_url: `https://${requireString(source, 'subdomain')}.zendesk.com/api/v2/`,
                auth: { type: 'bearer', token: '${accessToken}' },
                paginator: {
                    type: 'json_link',
                    next_url_path: 'next_page',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'tickets',
                    endpoint: {
                        path: 'tickets',
                        params: { per_page: 100 },
                        data_selector: 'tickets',
                    },
                },
                {
                    // Agents and admins, matched to a person by email.
                    // Replaced each sync so a removed agent drops out.
                    name: 'users',
                    write_disposition: 'replace',
                    endpoint: {
                        path: 'users',
                        params: {
                            per_page: 100,
                            'role[]': ['agent', 'admin'],
                        },
                        data_selector: 'users',
                        // A token that may not list people must not fail
                        // the sync; mapped people then see no records.
                        response_actions: [
                            { status_code: 401, action: 'ignore' },
                            { status_code: 403, action: 'ignore' },
                        ],
                    },
                },
            ],
        }),
    },
    [ConnectorType.AIRTABLE]: {
        displayName: 'Airtable',
        sourceFields: [
            accessToken('pat...'),
            textField('baseId', 'Base ID', 'appXXXXXXXXXXXXXX'),
            textField('table', 'Table', 'Orders'),
        ],
        oauth: null,
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://api.airtable.com/v0/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // Opaque `offset` returned in the body, echoed as a query param.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'offset',
                    cursor_param: 'offset',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'records',
                    endpoint: {
                        // A table may be named or referenced by id, and a
                        // name can contain spaces.
                        path: `${requireString(source, 'baseId')}/${encodeURIComponent(requireString(source, 'table'))}`,
                        params: { pageSize: 100 },
                        data_selector: 'records',
                    },
                },
            ],
        }),
    },
    [ConnectorType.CHARGEBEE]: {
        displayName: 'Chargebee',
        sourceFields: [
            {
                key: 'apiKey',
                label: 'API key',
                placeholder: 'live_...',
                secret: true,
                required: true,
            },
            textField('site', 'Site', 'acme'),
        ],
        oauth: null,
        buildSourceConfig: (source) => ({
            client: {
                base_url: `https://${requireString(source, 'site')}.chargebee.com/api/v2/`,
                // API key as the Basic username, with an empty password.
                auth: {
                    type: 'http_basic',
                    username: '${apiKey}',
                    password: '',
                },
                // Opaque `next_offset` in the body, echoed as `offset`.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'next_offset',
                    cursor_param: 'offset',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'subscriptions',
                    endpoint: {
                        path: 'subscriptions',
                        params: { limit: 100 },
                        // Each list entry wraps the resource in its own key.
                        data_selector: 'list[*].subscription',
                    },
                },
            ],
        }),
    },
    [ConnectorType.FRESHDESK]: {
        displayName: 'Freshdesk',
        sourceFields: [
            {
                key: 'apiKey',
                label: 'API key',
                placeholder: 'Freshdesk API key',
                secret: true,
                required: true,
            },
            textField('subdomain', 'Subdomain', 'acme'),
        ],
        oauth: null,
        // Plain-text twin of the HTML description. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'description_text',
        buildSourceConfig: (source) => ({
            client: {
                base_url: `https://${requireString(source, 'subdomain')}.freshdesk.com/api/v2/`,
                // API key as the Basic username; the password is a literal X.
                auth: {
                    type: 'http_basic',
                    username: '${apiKey}',
                    password: 'X',
                },
                // 1-based page number with no total in the body, so
                // pagination stops on the first empty page.
                paginator: {
                    type: 'page_number',
                    base_page: 1,
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'tickets',
                    endpoint: {
                        path: 'tickets',
                        params: {
                            per_page: 100,
                            updated_since: {
                                type: 'incremental',
                                cursor_path: 'updated_at',
                                initial_value: '2020-01-01T00:00:00Z',
                            },
                        },
                    },
                },
                {
                    // Agents, matched to a person by their contact email.
                    name: 'agents',
                    write_disposition: 'replace',
                    endpoint: {
                        path: 'agents',
                        params: { per_page: 100 },
                        // A token that may not list people must not fail
                        // the sync; mapped people then see no records.
                        response_actions: [
                            { status_code: 401, action: 'ignore' },
                            { status_code: 403, action: 'ignore' },
                        ],
                    },
                },
            ],
        }),
    },
    [ConnectorType.GITLAB]: {
        displayName: 'GitLab',
        sourceFields: [
            accessToken('glpat-...'),
            textField('project', 'Project', 'group/project'),
        ],
        oauth: null,
        // GitLab's own spelling for an issue body. Also a `feedback_text`
        // synonym, so the pack maps it without extra work.
        feedbackTextColumn: 'description',
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://gitlab.com/api/v4/',
                auth: {
                    type: 'api_key',
                    name: 'PRIVATE-TOKEN',
                    api_key: '${accessToken}',
                    location: 'header',
                },
                // GitLab paginates with RFC-5988 Link headers.
                paginator: { type: 'header_link' },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'issues',
                    endpoint: {
                        // A namespaced path must be URL-encoded whole.
                        path: `projects/${encodeURIComponent(requireString(source, 'project'))}/issues`,
                        params: {
                            per_page: 100,
                            // Ascending, so an interrupted sync resumes
                            // from the oldest unseen record.
                            order_by: 'updated_at',
                            sort: 'asc',
                            updated_after: {
                                type: 'incremental',
                                cursor_path: 'updated_at',
                                initial_value: '2020-01-01T00:00:00Z',
                            },
                        },
                    },
                },
                {
                    // Who can see confidential issues. Replaced each sync
                    // so a removed member loses access on the next run.
                    name: 'members',
                    write_disposition: 'replace',
                    endpoint: {
                        path: `projects/${encodeURIComponent(requireString(source, 'project'))}/members/all`,
                        params: { per_page: 100 },
                        // A token that may not list members must not fail
                        // the sync; confidential issues then fall back to
                        // author and assignees only.
                        response_actions: [
                            { status_code: 401, action: 'ignore' },
                            { status_code: 403, action: 'ignore' },
                            { status_code: 404, action: 'ignore' },
                        ],
                    },
                },
            ],
        }),
    },
    [ConnectorType.INTERCOM]: {
        displayName: 'Intercom',
        sourceFields: [accessToken('Intercom access token')],
        oauth: null,
        // Intercom's first-message body, nested under `source` and HTML — the
        // enrichment strips tags before scoring.
        // Dotted/`[*]` path, the same notation as `data_selector` above: the
        // enrichment mapper runs at extract, so it sees the provider's raw
        // nested JSON, not dlt's flattened `source__body` column.
        feedbackTextColumn: 'source.body',
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.intercom.io/',
                auth: { type: 'bearer', token: '${accessToken}' },
                headers: { 'Intercom-Version': '2.11' },
                // Cursor in a `pages` envelope, absent on the last page.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'pages.next.starting_after',
                    cursor_param: 'starting_after',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'conversations',
                    endpoint: {
                        path: 'conversations',
                        params: { per_page: 150 },
                        data_selector: 'conversations',
                    },
                },
                {
                    // Teammates, matched to a person by email.
                    name: 'admins',
                    write_disposition: 'replace',
                    endpoint: {
                        path: 'admins',
                        data_selector: 'admins',
                        // A token that may not list people must not fail
                        // the sync; mapped people then see no records.
                        response_actions: [
                            { status_code: 401, action: 'ignore' },
                            { status_code: 403, action: 'ignore' },
                        ],
                    },
                },
            ],
        }),
    },
    [ConnectorType.JIRA]: {
        displayName: 'Jira',
        sourceFields: [
            {
                key: 'apiToken',
                label: 'API token',
                placeholder: 'Atlassian API token',
                secret: true,
                required: true,
            },
            textField('email', 'Account email', 'you@acme.com'),
            textField('site', 'Site', 'acme'),
        ],
        oauth: null,
        buildSourceConfig: (source) => ({
            client: {
                base_url: `https://${requireString(source, 'site')}.atlassian.net/rest/api/3/`,
                // Atlassian API tokens authenticate as email + token.
                auth: {
                    type: 'http_basic',
                    username: '${email}',
                    password: '${apiToken}',
                },
                // Opaque nextPageToken, absent on the last page. The old
                // startAt/total offset search endpoint is gone from Cloud.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'nextPageToken',
                    cursor_param: 'nextPageToken',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'issues',
                    endpoint: {
                        path: 'search/jql',
                        params: {
                            // This endpoint returns id and key only unless
                            // fields are named.
                            fields: '*navigable',
                            maxResults: 100,
                            // Jira Cloud refuses unbounded JQL on this endpoint;
                            // a 1970 floor keeps every issue.
                            jql: 'updated >= "1970-01-01" order by updated asc',
                        },
                        data_selector: 'issues',
                    },
                },
            ],
        }),
    },
    [ConnectorType.NOTION]: {
        displayName: 'Notion',
        sourceFields: [
            accessToken('secret_...'),
            textField('databaseId', 'Database ID', '32-character database id'),
        ],
        oauth: null,
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://api.notion.com/v1/',
                auth: { type: 'bearer', token: '${accessToken}' },
                headers: { 'Notion-Version': '2022-06-28' },
                // Cursor returned in the body, sent back in the POST body.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'next_cursor',
                    cursor_body_path: 'start_cursor',
                    has_more_path: 'has_more',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'pages',
                    endpoint: {
                        path: `databases/${requireString(source, 'databaseId')}/query`,
                        method: 'POST',
                        json: { page_size: 100 },
                        data_selector: 'results',
                    },
                },
            ],
        }),
    },
    [ConnectorType.RAZORPAY]: {
        displayName: 'Razorpay',
        sourceFields: [
            {
                key: 'keyId',
                label: 'Key ID',
                placeholder: 'rzp_live_...',
                secret: true,
                required: true,
            },
            {
                key: 'keySecret',
                label: 'Key secret',
                placeholder: 'Razorpay key secret',
                secret: true,
                required: true,
            },
        ],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.razorpay.com/v1/',
                // Razorpay authenticates with HTTP Basic, not a bearer token.
                auth: {
                    type: 'http_basic',
                    username: '${keyId}',
                    password: '${keySecret}',
                },
                // skip/count offset. The envelope's `count` is the size of
                // the page, not the grand total, so it must not be a
                // total_path: pagination stops on the first empty page.
                paginator: {
                    type: 'offset',
                    limit: 100,
                    offset_param: 'skip',
                    limit_param: 'count',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'payments',
                    endpoint: {
                        path: 'payments',
                        // `created_at` is unix seconds, which no ISO
                        // incremental initial_value can seed.
                        data_selector: 'items',
                    },
                },
            ],
        }),
    },
    [ConnectorType.SHOPIFY]: {
        displayName: 'Shopify',
        sourceFields: [
            accessToken('shpat_...'),
            textField('shop', 'Shop', 'acme'),
        ],
        oauth: null,
        // Order note left by the buyer. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'note',
        buildSourceConfig: (source) => ({
            client: {
                base_url: `https://${requireString(source, 'shop')}.myshopify.com/admin/api/2024-10/`,
                auth: {
                    type: 'api_key',
                    name: 'X-Shopify-Access-Token',
                    api_key: '${accessToken}',
                    location: 'header',
                },
                // Cursor pagination surfaced as RFC-5988 Link headers.
                paginator: { type: 'header_link' },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'orders',
                    endpoint: {
                        path: 'orders.json',
                        params: {
                            limit: 250,
                            status: 'any',
                            updated_at_min: {
                                type: 'incremental',
                                cursor_path: 'updated_at',
                                initial_value: '2020-01-01T00:00:00Z',
                            },
                        },
                        data_selector: 'orders',
                    },
                },
            ],
        }),
    },
    [ConnectorType.STRIPE]: {
        displayName: 'Stripe',
        sourceFields: [
            {
                key: 'apiKey',
                label: 'Secret key',
                placeholder: 'sk_live_...',
                secret: true,
                required: true,
            },
        ],
        oauth: null,
        // Charge description. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'description',
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.stripe.com/v1/',
                auth: { type: 'bearer', token: '${apiKey}' },
                // `starting_after` takes the id of the last record on the
                // page, which the cursor paginator reads out of the body.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'data[-1].id',
                    cursor_param: 'starting_after',
                    has_more_path: 'has_more',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'charges',
                    endpoint: {
                        path: 'charges',
                        // A charge mutates after creation (refund, dispute),
                        // so a `created` cursor would miss those updates.
                        params: { limit: 100 },
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.PAGERDUTY]: {
        displayName: 'PagerDuty',
        sourceFields: [
            {
                key: 'apiKey',
                label: 'API key',
                placeholder: 'PagerDuty REST API key',
                secret: true,
                required: true,
            },
        ],
        oauth: null,
        // Incident summary text. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'description',
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.pagerduty.com/',
                // The scheme is a literal `Token token=` prefix, not Bearer.
                auth: {
                    type: 'api_key',
                    name: 'Authorization',
                    api_key: 'Token token=${apiKey}',
                    location: 'header',
                },
                // limit/offset. The envelope's `more` flag is not read: a
                // missing has_more_path raises, so stop on an empty page.
                paginator: {
                    type: 'offset',
                    limit: 100,
                    offset_param: 'offset',
                    limit_param: 'limit',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'incidents',
                    endpoint: {
                        path: 'incidents',
                        // Stated explicitly because the default filter
                        // omits resolved incidents. `since` filters on
                        // created_at and caps at a six-month window, so it
                        // is not an update cursor.
                        params: {
                            'statuses[]': [
                                'triggered',
                                'acknowledged',
                                'resolved',
                            ],
                        },
                        data_selector: 'incidents',
                    },
                },
            ],
        }),
    },
    [ConnectorType.KLAVIYO]: {
        displayName: 'Klaviyo',
        sourceFields: [
            {
                key: 'apiKey',
                label: 'Private API key',
                placeholder: 'pk_...',
                secret: true,
                required: true,
            },
        ],
        oauth: null,
        // Events over profiles: engagement events are the grain the
        // feedback-intelligence subsystem consumes; profiles are a slowly
        // changing dimension already covered by the CRM connectors.
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://a.klaviyo.com/api/',
                // Klaviyo's own scheme, not Bearer.
                auth: {
                    type: 'api_key',
                    name: 'Authorization',
                    api_key: 'Klaviyo-API-Key ${apiKey}',
                    location: 'header',
                },
                // The API is versioned by a required date header.
                headers: { revision: '2024-10-15' },
                // JSON:API: `links.next` is an absolute URL.
                paginator: {
                    type: 'json_link',
                    next_url_path: 'links.next',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'events',
                    endpoint: {
                        path: 'events',
                        // Incremental would need a `greater-than(...)`
                        // filter expression, which no incremental param
                        // builds, so this is a full refresh.
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.PIPEDRIVE]: {
        displayName: 'Pipedrive',
        sourceFields: [
            {
                key: 'apiToken',
                label: 'API token',
                placeholder: 'Pipedrive API token',
                secret: true,
                required: true,
            },
        ],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.pipedrive.com/v1/',
                auth: {
                    type: 'api_key',
                    name: 'api_token',
                    api_key: '${apiToken}',
                    location: 'query',
                },
                // start/limit offset. `more_items_in_collection` lives in
                // an envelope this paginator would raise on if absent.
                paginator: {
                    type: 'offset',
                    limit: 100,
                    offset_param: 'start',
                    limit_param: 'limit',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'deals',
                    endpoint: {
                        path: 'deals',
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.FRONT]: {
        displayName: 'Front',
        sourceFields: [accessToken('Front API token')],
        oauth: null,
        // Conversation subject; bodies live on messages. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'subject',
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api2.frontapp.com/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // `_pagination.next` is an absolute URL, null when done.
                paginator: {
                    type: 'json_link',
                    next_url_path: '_pagination.next',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'conversations',
                    endpoint: {
                        path: 'conversations',
                        params: { limit: 100 },
                        data_selector: '_results',
                    },
                },
            ],
        }),
    },
    [ConnectorType.HELP_SCOUT]: {
        displayName: 'Help Scout',
        sourceFields: [
            {
                key: 'appId',
                label: 'App ID',
                placeholder: 'Help Scout app id',
                secret: true,
                required: true,
            },
            {
                key: 'appSecret',
                label: 'App secret',
                placeholder: 'Help Scout app secret',
                secret: true,
                required: true,
            },
        ],
        // Client credentials, not an authorization-code flow: dlt mints and
        // renews the token itself, so there is nothing for us to refresh.
        oauth: null,
        // Flat text preview; message bodies need a second request. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'preview',
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.helpscout.net/v2/',
                auth: {
                    type: 'oauth2_client_credentials',
                    access_token_url: 'https://api.helpscout.net/v2/oauth2/token',
                    client_id: '${appId}',
                    client_secret: '${appSecret}',
                },
                // HAL: `_links.next.href` is an absolute URL.
                paginator: {
                    type: 'json_link',
                    next_url_path: '_links.next.href',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'conversations',
                    endpoint: {
                        path: 'conversations',
                        params: { status: 'all' },
                        data_selector: '_embedded.conversations',
                    },
                },
            ],
        }),
    },
    [ConnectorType.ASANA]: {
        displayName: 'Asana',
        sourceFields: [
            accessToken('Asana personal access token'),
            textField('projectGid', 'Project ID', '1200000000000000'),
        ],
        oauth: null,
        // Asana's name for a task body. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'notes',
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://app.asana.com/api/1.0/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // Opaque `next_page.offset`, absent on the last page.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'next_page.offset',
                    cursor_param: 'offset',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'gid',
            },
            resources: [
                {
                    name: 'tasks',
                    endpoint: {
                        // A bare /tasks needs a project or assignee filter.
                        path: `projects/${requireString(source, 'projectGid')}/tasks`,
                        params: {
                            limit: 100,
                            opt_fields: 'modified_at,name,completed,assignee,assignee.email',
                            modified_since: {
                                type: 'incremental',
                                cursor_path: 'modified_at',
                                initial_value: '2020-01-01T00:00:00Z',
                            },
                        },
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.CLICKUP]: {
        displayName: 'ClickUp',
        sourceFields: [
            accessToken('pk_...'),
            textField('listId', 'List ID', '900000000000'),
        ],
        oauth: null,
        // ClickUp task body. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'description',
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://api.clickup.com/api/v2/',
                // The token is sent bare, with no auth scheme prefix.
                auth: {
                    type: 'api_key',
                    name: 'Authorization',
                    api_key: '${accessToken}',
                    location: 'header',
                },
                // 0-based page number; stops on the first empty page.
                paginator: {
                    type: 'page_number',
                    base_page: 0,
                    page_param: 'page',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'tasks',
                    endpoint: {
                        path: `list/${requireString(source, 'listId')}/task`,
                        // `date_updated_gt` is unix milliseconds, which no
                        // ISO incremental initial_value can seed.
                        params: { subtasks: true },
                        data_selector: 'tasks',
                    },
                },
            ],
        }),
    },
    [ConnectorType.BAMBOOHR]: {
        displayName: 'BambooHR',
        sourceFields: [
            {
                key: 'apiKey',
                label: 'API key',
                placeholder: 'BambooHR API key',
                secret: true,
                required: true,
            },
            textField('subdomain', 'Subdomain', 'acme'),
        ],
        oauth: null,
        buildSourceConfig: (source) => ({
            client: {
                base_url: `https://api.bamboohr.com/api/gateway.php/${requireString(source, 'subdomain')}/v1/`,
                // API key as the Basic username; the password is ignored.
                auth: {
                    type: 'http_basic',
                    username: '${apiKey}',
                    password: 'x',
                },
                // This API answers in XML unless JSON is asked for.
                headers: { Accept: 'application/json' },
                // The directory returns every employee in one response.
                paginator: { type: 'single_page' },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'employees',
                    endpoint: {
                        path: 'employees/directory',
                        data_selector: 'employees',
                    },
                },
            ],
        }),
    },
    [ConnectorType.GREENHOUSE]: {
        displayName: 'Greenhouse',
        sourceFields: [
            {
                key: 'apiKey',
                label: 'Harvest API key',
                placeholder: 'Greenhouse Harvest API key',
                secret: true,
                required: true,
            },
        ],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://harvest.greenhouse.io/v1/',
                // API key as the Basic username, with an empty password.
                auth: {
                    type: 'http_basic',
                    username: '${apiKey}',
                    password: '',
                },
                // Harvest paginates with RFC-5988 Link headers.
                paginator: { type: 'header_link' },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'applications',
                    endpoint: {
                        // The response is a bare array, so no selector.
                        path: 'applications',
                        params: { per_page: 100 },
                    },
                },
            ],
        }),
    },
    [ConnectorType.LEVER]: {
        displayName: 'Lever',
        sourceFields: [
            {
                key: 'apiKey',
                label: 'API key',
                placeholder: 'Lever API key',
                secret: true,
                required: true,
            },
        ],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.lever.co/v1/',
                // API key as the Basic username, with an empty password.
                auth: {
                    type: 'http_basic',
                    username: '${apiKey}',
                    password: '',
                },
                // Opaque `next` token echoed as `offset`, absent when done.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'next',
                    cursor_param: 'offset',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'opportunities',
                    endpoint: {
                        path: 'opportunities',
                        // `updated_at_start` is unix milliseconds, which no
                        // ISO incremental initial_value can seed.
                        params: { limit: 100 },
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.TYPEFORM]: {
        displayName: 'Typeform',
        sourceFields: [
            accessToken('tfp_...'),
            textField('formId', 'Form ID', 'AbCdEf12'),
        ],
        oauth: null,
        // Every free-text answer on a response, scored together so one response
        // gets one sentiment rather than one per question.
        // Dotted/`[*]` path, the same notation as `data_selector` above: the
        // enrichment mapper runs at extract, so it sees the provider's raw
        // nested JSON, not dlt's flattened `source__body` column.
        feedbackTextColumn: 'answers[*].text',
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://api.typeform.com/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // Seeks past the last response token on the page, the same
                // shape as Stripe's `starting_after`. An empty page yields
                // no token, which ends the walk.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'items[-1].token',
                    cursor_param: 'after',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'response_id',
            },
            resources: [
                {
                    name: 'responses',
                    endpoint: {
                        path: `forms/${requireString(source, 'formId')}/responses`,
                        params: { page_size: 1000 },
                        data_selector: 'items',
                    },
                },
            ],
        }),
    },
    [ConnectorType.POSTHOG]: {
        displayName: 'PostHog',
        sourceFields: [
            accessToken('phx_...'),
            textField('host', 'Host', 'eu.posthog.com'),
            textField('projectId', 'Project ID', '12345'),
        ],
        oauth: null,
        buildSourceConfig: (source) => ({
            client: {
                // Self-hosted and the two clouds differ only by host.
                base_url: `https://${requireString(source, 'host')}/api/`,
                auth: { type: 'bearer', token: '${accessToken}' },
                // `next` is an absolute URL, null on the last page.
                paginator: {
                    type: 'json_link',
                    next_url_path: 'next',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'events',
                    endpoint: {
                        path: `projects/${requireString(source, 'projectId')}/events/`,
                        // An event is immutable, so there is nothing an
                        // updated-at cursor would pick up on a re-sync.
                        data_selector: 'results',
                    },
                },
            ],
        }),
    },
    [ConnectorType.GOOGLE_DRIVE]: {
        displayName: 'Google Drive',
        sourceFields: [accessToken('Google OAuth access token')],
        oauth: {
            tokenUrl: 'https://oauth2.googleapis.com/token',
            authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
            scopes: [
                'https://www.googleapis.com/auth/drive.metadata.readonly',
            ],
            usePkce: true,
            // Google returns a refresh token only for access_type=offline,
            // and only on the *first* consent — so prompt=consent too, or a
            // reconnect silently yields an access token with no renewal.
            authorizeParams: {
                access_type: 'offline',
                prompt: 'consent',
            },
            extraParams: {},
        },
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://www.googleapis.com/drive/v3/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // Opaque nextPageToken echoed as pageToken, absent at the end.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'nextPageToken',
                    cursor_param: 'pageToken',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'files',
                    endpoint: {
                        path: 'files',
                        // Drive returns a minimal field set unless asked.
                        // `orderBy` cannot seed an incremental, and
                        // modifiedTime filtering lives in the `q` DSL.
                        params: {
                            pageSize: 1000,
                            fields: 'nextPageToken,files(id,name,mimeType,modifiedTime,createdTime,owners,size)',
                        },
                        data_selector: 'files',
                    },
                },
            ],
        }),
    },
    [ConnectorType.GMAIL]: {
        displayName: 'Gmail',
        sourceFields: [accessToken('Google OAuth access token')],
        // Same authorization server, PKCE, and offline+consent params as
        // Google Drive — different scope for a different Google product.
        oauth: {
            tokenUrl: 'https://oauth2.googleapis.com/token',
            authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
            scopes: ['https://www.googleapis.com/auth/gmail.readonly'],
            usePkce: true,
            authorizeParams: {
                access_type: 'offline',
                prompt: 'consent',
            },
            extraParams: {},
        },
        // Flat plain-text excerpt; the full body is nested MIME parts. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'snippet',
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://gmail.googleapis.com/gmail/v1/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // Opaque nextPageToken echoed as pageToken, absent on the
                // last page — same shape as Drive, confirmed against the
                // live users.messages.list docs.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'nextPageToken',
                    cursor_param: 'pageToken',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'messages',
                    endpoint: {
                        path: 'users/me/messages',
                        // The list endpoint returns id/threadId only; the
                        // `message_details` child below fetches the rest. No
                        // date param exists; `q` is a free-text search DSL,
                        // not an incremental cursor.
                        params: { maxResults: 500 },
                        data_selector: 'messages',
                    },
                },
                {
                    // One messages.get per message: snippet, internalDate and
                    // labelIds land as columns, headers in a child table.
                    // ponytail: one request per message, batch endpoint if large inboxes are slow.
                    name: 'message_details',
                    endpoint: {
                        path: 'users/me/messages/{resources.messages.id}',
                        params: {
                            format: 'metadata',
                            metadataHeaders: [
                                'Subject',
                                'From',
                                'To',
                                'Date',
                            ],
                        },
                        paginator: 'single_page',
                    },
                },
            ],
        }),
    },
    [ConnectorType.GOOGLE_CALENDAR]: {
        displayName: 'Google Calendar',
        sourceFields: [accessToken('Google OAuth access token')],
        oauth: googleOAuth([
            'https://www.googleapis.com/auth/calendar.readonly',
        ]),
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://www.googleapis.com/calendar/v3/',
                auth: { type: 'bearer', token: '${accessToken}' },
                paginator: googleCursor,
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'events',
                    endpoint: {
                        path: 'calendars/primary/events',
                        // singleEvents expands recurring events into one row
                        // per occurrence, each with its own id.
                        params: { singleEvents: true, maxResults: 2500 },
                        data_selector: 'items',
                    },
                },
            ],
        }),
    },
    [ConnectorType.GOOGLE_SHEETS]: {
        displayName: 'Google Sheets',
        sourceFields: [accessToken('Google OAuth access token')],
        oauth: googleOAuth([
            'https://www.googleapis.com/auth/drive.metadata.readonly',
            'https://www.googleapis.com/auth/spreadsheets.readonly',
        ]),
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://www.googleapis.com/drive/v3/',
                auth: { type: 'bearer', token: '${accessToken}' },
                paginator: googleCursor,
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                googleDriveListing('spreadsheets', 'application/vnd.google-apps.spreadsheet'),
                {
                    // Values of the first tab; rows land in a child table.
                    // ponytail: first tab, first 1000 rows only; list tabs via spreadsheets.get if needed.
                    name: 'sheet_values',
                    primary_key: '_spreadsheets_id',
                    include_from_parent: ['id', 'name'],
                    endpoint: {
                        path: 'https://sheets.googleapis.com/v4/spreadsheets/{resources.spreadsheets.id}/values/A1:Z1000',
                        // The whole response is one row; without '$' dlt picks `values` and treats each row array as a record.
                        data_selector: '$',
                        paginator: 'single_page',
                    },
                },
            ],
        }),
    },
    [ConnectorType.GOOGLE_DOCS]: {
        displayName: 'Google Docs',
        sourceFields: [accessToken('Google OAuth access token')],
        oauth: googleOAuth([
            'https://www.googleapis.com/auth/drive.metadata.readonly',
            'https://www.googleapis.com/auth/documents.readonly',
        ]),
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://www.googleapis.com/drive/v3/',
                auth: { type: 'bearer', token: '${accessToken}' },
                paginator: googleCursor,
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                googleDriveListing('documents', 'application/vnd.google-apps.document'),
                {
                    // Full document; paragraph text lands in nested child
                    // tables under body.content[].paragraph.elements[].
                    name: 'document_content',
                    primary_key: 'documentId',
                    endpoint: {
                        path: 'https://docs.googleapis.com/v1/documents/{resources.documents.id}',
                        paginator: 'single_page',
                    },
                },
            ],
        }),
    },
    [ConnectorType.GOOGLE_WORKSPACE_DIRECTORY]: {
        displayName: 'Google Workspace Directory',
        sourceFields: [accessToken('Google OAuth access token')],
        oauth: {
            tokenUrl: 'https://oauth2.googleapis.com/token',
            authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
            scopes: [
                'https://www.googleapis.com/auth/admin.directory.user.readonly',
            ],
            usePkce: true,
            // Same authorization server as Google Drive above.
            authorizeParams: {
                access_type: 'offline',
                prompt: 'consent',
            },
            extraParams: {},
        },
        buildSourceConfig: () => ({
            client: {
                // Admin SDK. A Workspace account only — a personal Gmail
                // account has no directory to read.
                base_url: 'https://admin.googleapis.com/admin/directory/v1/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // Opaque nextPageToken echoed as pageToken.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'nextPageToken',
                    cursor_param: 'pageToken',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'users',
                    endpoint: {
                        path: 'users',
                        // `my_customer` resolves to the authorizing admin's
                        // own account, so no customer id is collected.
                        params: {
                            customer: 'my_customer',
                            maxResults: 500,
                        },
                        data_selector: 'users',
                    },
                },
            ],
        }),
    },
    [ConnectorType.SALESFORCE]: {
        displayName: 'Salesforce',
        sourceFields: [
            accessToken('Salesforce OAuth access token'),
            textField('instanceDomain', 'Instance domain', 'acme.my.salesforce.com'),
        ],
        oauth: {
            tokenUrl: 'https://login.salesforce.com/services/oauth2/token',
            authorizeUrl: 'https://login.salesforce.com/services/oauth2/authorize',
            // `refresh_token` is the scope that makes the grant renewable.
            scopes: ['api', 'refresh_token'],
            usePkce: true,
            // The renewable stance is the scope above, not a param.
            authorizeParams: {},
            extraParams: {},
        },
        // Opportunity description. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'description',
        buildSourceConfig: (source) => ({
            client: {
                // Every org answers on its own instance host.
                base_url: `https://${requireString(source, 'instanceDomain')}/services/data/v60.0/`,
                auth: { type: 'bearer', token: '${accessToken}' },
                // `nextRecordsUrl` is a path, resolved against the request
                // URL, and is absent once `done` is true.
                paginator: {
                    type: 'json_link',
                    next_url_path: 'nextRecordsUrl',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'Id',
            },
            resources: [
                {
                    name: 'opportunities',
                    endpoint: {
                        path: 'query',
                        // The cursor would have to live inside the SOQL
                        // string, which no incremental param expresses —
                        // the same constraint as Jira's JQL.
                        params: {
                            q: 'SELECT Id, Name, Amount, StageName, CloseDate, IsWon, IsClosed, AccountId, OwnerId, CreatedDate, LastModifiedDate FROM Opportunity ORDER BY LastModifiedDate ASC',
                        },
                        data_selector: 'records',
                    },
                },
                {
                    // Who owns each opportunity, matched to a person by
                    // email. Replaced each sync so a removed user drops out.
                    name: 'users',
                    write_disposition: 'replace',
                    endpoint: {
                        path: 'query',
                        params: {
                            q: 'SELECT Id, Email, IsActive FROM User',
                        },
                        data_selector: 'records',
                        // A login that may not read users must not fail
                        // the sync; mapped people then see no opportunities.
                        response_actions: [
                            { status_code: 401, action: 'ignore' },
                            { status_code: 403, action: 'ignore' },
                        ],
                    },
                },
            ],
        }),
    },
    [ConnectorType.SLACK]: {
        displayName: 'Slack',
        sourceFields: [accessToken('xoxb-...')],
        oauth: {
            tokenUrl: 'https://slack.com/api/oauth.v2.access',
            authorizeUrl: 'https://slack.com/oauth/v2/authorize',
            scopes: ['channels:read', 'channels:history', 'users:read'],
            // Slack's v2 flow documents no PKCE support.
            usePkce: false,
            authorizeParams: {},
            extraParams: {},
        },
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://slack.com/api/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // `response_metadata.next_cursor` comes back as an empty
                // string on the last page, which reads as no cursor.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'response_metadata.next_cursor',
                    cursor_param: 'cursor',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'conversations',
                    endpoint: {
                        // Slack answers 200 with `ok: false` on failure, so
                        // an auth or scope error surfaces as an empty load
                        // rather than as a failed sync.
                        path: 'conversations.list',
                        params: { limit: 200, exclude_archived: false },
                        data_selector: 'channels',
                    },
                },
                {
                    name: 'users',
                    endpoint: {
                        path: 'users.list',
                        params: { limit: 200 },
                        data_selector: 'members',
                    },
                },
                {
                    // Top-level messages only; `ts` is unique within a channel.
                    // ponytail: no thread replies, add conversations.replies when needed.
                    name: 'messages',
                    primary_key: ['_conversations_id', 'ts'],
                    include_from_parent: ['id'],
                    endpoint: {
                        path: 'conversations.history',
                        params: {
                            channel: '{resources.conversations.id}',
                            limit: 200,
                        },
                        data_selector: 'messages',
                        // A channel the bot has not joined answers 200 with
                        // `not_in_channel`; skip it rather than the sync.
                        // Quotes inside message text are escaped, so a
                        // successful page cannot match.
                        response_actions: [
                            {
                                status_code: 200,
                                content: '"error":"not_in_channel"',
                                action: 'ignore',
                            },
                        ],
                    },
                },
            ],
        }),
    },
    [ConnectorType.MICROSOFT_GRAPH]: {
        displayName: 'Microsoft 365',
        sourceFields: [
            accessToken('Microsoft OAuth access token'),
            textField('tenantId', 'Tenant ID', 'contoso.onmicrosoft.com'),
        ],
        oauth: {
            // The tenant segment is per-customer, so both URLs interpolate
            // it the way Zendesk interpolates its subdomain.
            tokenUrl: 'https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token',
            authorizeUrl: 'https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize',
            // `offline_access` is what makes the grant renewable here.
            scopes: [
                'https://graph.microsoft.com/User.Read.All',
                'offline_access',
            ],
            usePkce: true,
            // The renewable stance is the scope above, not a param.
            authorizeParams: {},
            extraParams: {},
        },
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://graph.microsoft.com/v1.0/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // OData: `@odata.nextLink` is an absolute URL. The key
                // contains a dot, so the path is quoted to stop jsonpath
                // reading it as two segments.
                paginator: {
                    type: 'json_link',
                    next_url_path: '"@odata.nextLink"',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'users',
                    endpoint: {
                        path: 'users',
                        // Graph exposes no updated-at on a user, so there is
                        // no cursor to track.
                        params: { $top: 999 },
                        data_selector: 'value',
                    },
                },
            ],
        }),
    },
    [ConnectorType.XERO]: {
        displayName: 'Xero',
        sourceFields: [
            accessToken('Xero OAuth access token'),
            textField('tenantId', 'Tenant ID', 'Xero organisation tenant id'),
        ],
        oauth: {
            tokenUrl: 'https://identity.xero.com/connect/token',
            authorizeUrl: 'https://login.xero.com/identity/connect/authorize',
            scopes: ['accounting.transactions.read', 'offline_access'],
            usePkce: true,
            // The renewable stance is the scope above, not a param.
            authorizeParams: {},
            extraParams: {},
        },
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.xero.com/api.xro/2.0/',
                auth: { type: 'bearer', token: '${accessToken}' },
                headers: {
                    // Which organisation the token should act on, and JSON
                    // rather than this API's default XML.
                    'Xero-Tenant-Id': '${tenantId}',
                    Accept: 'application/json',
                },
                // 1-based page number with no total in the body, so
                // pagination stops on the first empty page.
                paginator: {
                    type: 'page_number',
                    base_page: 1,
                    page_param: 'page',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'InvoiceID',
            },
            resources: [
                {
                    name: 'invoices',
                    endpoint: {
                        path: 'Invoices',
                        // Xero's update cursor is the If-Modified-Since
                        // header, not a query param, so no incremental.
                        data_selector: 'Invoices',
                    },
                },
            ],
        }),
    },
    [ConnectorType.ZOHO_CRM]: {
        displayName: 'Zoho CRM',
        sourceFields: [
            accessToken('Zoho OAuth access token'),
            textField('region', 'Data centre', 'com'),
        ],
        oauth: {
            // Zoho pins an account to one data centre: com, eu, in, com.au.
            tokenUrl: 'https://accounts.zoho.${region}/oauth/v2/token',
            authorizeUrl: 'https://accounts.zoho.${region}/oauth/v2/auth',
            scopes: ['ZohoCRM.modules.deals.READ'],
            usePkce: true,
            // Same shape as Google: offline for the refresh token, consent
            // because Zoho prompts only on the first authorization.
            authorizeParams: {
                access_type: 'offline',
                prompt: 'consent',
            },
            extraParams: {},
        },
        // Deal description. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'description',
        buildSourceConfig: (source) => ({
            client: {
                base_url: `https://www.zohoapis.${requireString(source, 'region')}/crm/v6/`,
                // Zoho's own scheme, not Bearer.
                auth: {
                    type: 'api_key',
                    name: 'Authorization',
                    api_key: 'Zoho-oauthtoken ${accessToken}',
                    location: 'header',
                },
                // 1-based page number. `info.more_records` is the real flag
                // but an exhausted list answers 204 with no body at all, so
                // stopping on an empty page is the safer read.
                paginator: {
                    type: 'page_number',
                    base_page: 1,
                    page_param: 'page',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'deals',
                    endpoint: {
                        path: 'Deals',
                        // The update cursor is the If-Modified-Since
                        // header here too, so no incremental param.
                        params: { per_page: 200 },
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.ZOHO_BOOKS]: {
        displayName: 'Zoho Books',
        sourceFields: [
            accessToken('Zoho OAuth access token'),
            textField('region', 'Data centre', 'com'),
            textField('organizationId', 'Organization ID', '10234695'),
        ],
        // Same OAuth shape as Zoho CRM — same accounts host, same
        // offline+consent params — different product scope.
        oauth: {
            tokenUrl: 'https://accounts.zoho.${region}/oauth/v2/token',
            authorizeUrl: 'https://accounts.zoho.${region}/oauth/v2/auth',
            scopes: ['ZohoBooks.invoices.READ'],
            usePkce: true,
            authorizeParams: {
                access_type: 'offline',
                prompt: 'consent',
            },
            extraParams: {},
        },
        buildSourceConfig: (source) => ({
            client: {
                base_url: `https://www.zohoapis.${requireString(source, 'region')}/books/v3/`,
                auth: {
                    type: 'api_key',
                    name: 'Authorization',
                    api_key: 'Zoho-oauthtoken ${accessToken}',
                    location: 'header',
                },
                // 1-based page number, same as Zoho CRM. The real flag is
                // page_context.has_more_page, which no shipped paginator
                // inspects, so pagination stops on the first empty page
                // instead — confirmed against the live API docs.
                paginator: {
                    type: 'page_number',
                    base_page: 1,
                    page_param: 'page',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'invoice_id',
            },
            resources: [
                {
                    name: 'invoices',
                    endpoint: {
                        path: 'invoices',
                        // organization_id is mandatory on every Books call.
                        params: {
                            per_page: 200,
                            organization_id: requireString(source, 'organizationId'),
                        },
                        data_selector: 'invoices',
                    },
                },
            ],
        }),
    },
    [ConnectorType.CALENDLY]: {
        displayName: 'Calendly',
        sourceFields: [
            accessToken('Calendly OAuth access token'),
            textField('organizationUri', 'Organization URI', 'https://api.calendly.com/organizations/AAAAAAAAAAAAAAAA'),
        ],
        oauth: {
            tokenUrl: 'https://auth.calendly.com/oauth/token',
            authorizeUrl: 'https://auth.calendly.com/oauth/authorize',
            // Calendly's flow takes no scope parameter.
            scopes: [],
            usePkce: false,
            authorizeParams: {},
            extraParams: {},
        },
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.calendly.com/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // Opaque `pagination.next_page_token`, absent at the end.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'pagination.next_page_token',
                    cursor_param: 'page_token',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'uri',
            },
            resources: [
                {
                    name: 'scheduled_events',
                    endpoint: {
                        path: 'scheduled_events',
                        // This endpoint requires an organization or user
                        // scope; it rejects an unscoped call.
                        params: {
                            organization: '${organizationUri}',
                            count: 100,
                        },
                        data_selector: 'collection',
                    },
                },
            ],
        }),
    },
    [ConnectorType.MAILCHIMP]: {
        displayName: 'Mailchimp',
        sourceFields: [
            secretField('apiKey', 'API key', 'Mailchimp API key'),
            textField('serverPrefix', 'Server prefix', 'us14'),
        ],
        // Static key rather than OAuth on purpose: Mailchimp's OAuth flow
        // hands back a token plus a metadata endpoint that must be called
        // to discover the account's server prefix, and that extra call is
        // not something a manifest entry can express.
        oauth: null,
        buildSourceConfig: (source) => ({
            client: {
                // The server prefix is part of the host, and it is also the
                // suffix of the API key itself.
                base_url: `https://${requireString(source, 'serverPrefix')}.api.mailchimp.com/3.0/`,
                // Any non-empty username works; the key is the password.
                auth: {
                    type: 'http_basic',
                    username: 'lightbox',
                    password: '${apiKey}',
                },
                // count/offset with a real grand total in the envelope.
                paginator: {
                    type: 'offset',
                    limit: 1000,
                    offset_param: 'offset',
                    limit_param: 'count',
                    total_path: 'total_items',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'campaigns',
                    endpoint: {
                        path: 'campaigns',
                        // `since_send_time` filters on send time, not on
                        // last update, so it is not an update cursor.
                        data_selector: 'campaigns',
                    },
                },
            ],
        }),
    },
    [ConnectorType.META_ADS]: {
        displayName: 'Meta Ads',
        sourceFields: [
            accessToken('Meta access token'),
            textField('adAccountId', 'Ad account ID', 'act_1234567890'),
        ],
        // No OAuth: Meta issues no refresh token in any flow, so the
        // RFC-6749 refresh_token grant this platform performs cannot work.
        // Its own fb_exchange_token grant swaps a short-lived token for a
        // ~60-day one, but re-exchanging an already-long-lived *user* token
        // is undocumented and an expired one cannot be exchanged at all —
        // so no unattended refresher can keep a user token alive. Paste a
        // System User token instead: generated in Business Manager, it is
        // long-lived by default and is the supported unattended path.
        oauth: null,
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://graph.facebook.com/v21.0/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // `paging.next` is an absolute URL, absent on the last page.
                paginator: {
                    type: 'json_link',
                    next_url_path: 'paging.next',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'campaigns',
                    endpoint: {
                        path: `${requireString(source, 'adAccountId')}/campaigns`,
                        // Graph returns ids only unless fields are named.
                        // `updated_time` is filterable only through the
                        // `filtering` DSL, so this is a full refresh.
                        params: {
                            limit: 100,
                            fields: 'id,name,status,objective,daily_budget,lifetime_budget,created_time,updated_time',
                        },
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.CONFLUENCE]: {
        displayName: 'Atlassian Confluence',
        sourceFields: [
            secretField('apiToken', 'API token', 'Atlassian API token'),
            textField('email', 'Account email', 'you@acme.com'),
            textField('site', 'Site', 'acme'),
        ],
        // Email plus API token, mirroring Jira. Atlassian's 3LO flow would
        // authorize against api.atlassian.com and then need a cloud-id
        // lookup to build the real base URL, which is an extra call a
        // manifest entry cannot make.
        oauth: null,
        buildSourceConfig: (source) => ({
            client: {
                base_url: `https://${requireString(source, 'site')}.atlassian.net/wiki/api/v2/`,
                auth: {
                    type: 'http_basic',
                    username: '${email}',
                    password: '${apiToken}',
                },
                // v2 returns a relative `_links.next`, resolved against the
                // request URL, and omits it on the last page.
                paginator: {
                    type: 'json_link',
                    next_url_path: '_links.next',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'pages',
                    endpoint: {
                        path: 'pages',
                        // v2 exposes no updated-at filter on this endpoint.
                        params: { limit: 250 },
                        data_selector: 'results',
                    },
                },
            ],
        }),
    },
    [ConnectorType.BITBUCKET]: {
        displayName: 'Bitbucket Cloud',
        sourceFields: [
            textField('username', 'Username', 'your-bitbucket-username'),
            secretField('appPassword', 'App password', 'Bitbucket app password'),
            textField('workspace', 'Workspace', 'acme'),
            textField('repoSlug', 'Repository slug', 'acme-api'),
        ],
        // App passwords authenticate as username + password, same shape as
        // Jira/Confluence's email + API token.
        oauth: null,
        // Pull request body. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'description',
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://api.bitbucket.org/2.0/',
                auth: {
                    type: 'http_basic',
                    username: '${username}',
                    password: '${appPassword}',
                },
                // `next` is an absolute URL, absent on the last page —
                // confirmed against the live v2.0 pull-requests response.
                paginator: {
                    type: 'json_link',
                    next_url_path: 'next',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'pullrequests',
                    endpoint: {
                        path: `repositories/${requireString(source, 'workspace')}/${requireString(source, 'repoSlug')}/pullrequests`,
                        // No updated-since filter on this endpoint; state
                        // is queryable but not date-filterable.
                        params: { pagelen: 50, state: 'ALL' },
                        data_selector: 'values',
                    },
                },
            ],
        }),
    },
    [ConnectorType.TWILIO]: {
        displayName: 'Twilio',
        sourceFields: [
            // The SID is an account identifier, not an authentication
            // factor, and it is interpolated into the path — so it is a
            // plain field while the auth token is the secret.
            textField('accountSid', 'Account SID', 'AC...'),
            secretField('authToken', 'Auth token', 'Twilio auth token'),
        ],
        oauth: null,
        // The SMS text itself. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'body',
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://api.twilio.com/2010-04-01/',
                auth: {
                    type: 'http_basic',
                    username: '${accountSid}',
                    password: '${authToken}',
                },
                // `next_page_uri` is a relative path, resolved against the
                // request URL, and is null on the last page.
                paginator: {
                    type: 'json_link',
                    next_url_path: 'next_page_uri',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'sid',
            },
            resources: [
                {
                    name: 'messages',
                    endpoint: {
                        path: `Accounts/${requireString(source, 'accountSid')}/Messages.json`,
                        // The date filters here are send-date only, so
                        // there is no update cursor to track.
                        params: { PageSize: 1000 },
                        data_selector: 'messages',
                    },
                },
            ],
        }),
    },
    [ConnectorType.RECURLY]: {
        displayName: 'Recurly',
        sourceFields: [secretField('apiKey', 'API key', 'Recurly API key')],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://v3.recurly.com/',
                // API key as the Basic username, with an empty password.
                auth: {
                    type: 'http_basic',
                    username: '${apiKey}',
                    password: '',
                },
                // This API is versioned by the Accept header.
                headers: {
                    Accept: 'application/vnd.recurly.v2021-02-25',
                },
                // `next` is a relative path carrying an opaque cursor,
                // resolved against the request URL, absent when done.
                paginator: {
                    type: 'json_link',
                    next_url_path: 'next',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'subscriptions',
                    endpoint: {
                        path: 'subscriptions',
                        // Sorted ascending by update time, but the cursor
                        // is opaque rather than a timestamp param.
                        params: {
                            limit: 200,
                            sort: 'updated_at',
                            order: 'asc',
                        },
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.GOCARDLESS]: {
        displayName: 'GoCardless',
        sourceFields: [accessToken('live_...')],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.gocardless.com/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // A required version header, not a URL path segment.
                headers: { 'GoCardless-Version': '2015-07-06' },
                // Opaque `meta.cursors.after`, null on the last page.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'meta.cursors.after',
                    cursor_param: 'after',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'payments',
                    endpoint: {
                        path: 'payments',
                        // The date filters are creation-time only, and a
                        // payment's status changes after creation.
                        params: { limit: 500 },
                        data_selector: 'payments',
                    },
                },
            ],
        }),
    },
    [ConnectorType.SQUARE]: {
        displayName: 'Square',
        sourceFields: [accessToken('Square access token')],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://connect.squareup.com/v2/',
                auth: { type: 'bearer', token: '${accessToken}' },
                headers: { 'Square-Version': '2024-10-17' },
                // Opaque `cursor` in the body, echoed as a `cursor` param
                // and absent once the list is exhausted.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'cursor',
                    cursor_param: 'cursor',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'payments',
                    endpoint: {
                        path: 'payments',
                        // `begin_time` filters on creation, and a payment
                        // mutates afterwards (refund, dispute).
                        params: { limit: 100 },
                        data_selector: 'payments',
                    },
                },
            ],
        }),
    },
    [ConnectorType.HIBOB]: {
        displayName: 'HiBob',
        sourceFields: [
            secretField('serviceUserId', 'Service user ID', 'HiBob service user id'),
            secretField('serviceUserToken', 'Service user token', 'HiBob service user token'),
        ],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.hibob.com/v1/',
                auth: {
                    type: 'http_basic',
                    username: '${serviceUserId}',
                    password: '${serviceUserToken}',
                },
                // The people search returns the whole directory in one
                // response — there is no pagination to follow.
                paginator: { type: 'single_page' },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'employees',
                    endpoint: {
                        // A POST search; the older GET /people is retired.
                        path: 'people/search',
                        method: 'POST',
                        json: { showInactive: false },
                        data_selector: 'employees',
                    },
                },
            ],
        }),
    },
    [ConnectorType.TRELLO]: {
        displayName: 'Trello',
        sourceFields: [
            secretField('apiKey', 'API key', 'Trello API key'),
            secretField('apiToken', 'API token', 'Trello API token'),
            textField('boardId', 'Board ID', '5f2b...'),
        ],
        oauth: null,
        // Trello's abbreviated card body. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'desc',
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://api.trello.com/1/',
                // Both credentials travel as query params. dlt's api_key
                // auth carries one, so the token rides in the endpoint
                // params — still a placeholder the runner resolves.
                auth: {
                    type: 'api_key',
                    name: 'key',
                    api_key: '${apiKey}',
                    location: 'query',
                },
                // This endpoint returns the board's cards in one response.
                paginator: { type: 'single_page' },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'cards',
                    endpoint: {
                        path: `boards/${requireString(source, 'boardId')}/cards`,
                        params: { token: '${apiToken}' },
                    },
                },
            ],
        }),
    },
    [ConnectorType.WRIKE]: {
        displayName: 'Wrike',
        sourceFields: [accessToken('Wrike permanent access token')],
        oauth: null,
        // Wrike task body. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'description',
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://www.wrike.com/api/v4/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // Opaque `nextPageToken`, returned only when a pageSize is
                // asked for, and absent on the last page.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'nextPageToken',
                    cursor_param: 'nextPageToken',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'tasks',
                    endpoint: {
                        path: 'tasks',
                        // `updatedDate` takes an ISO interval string rather
                        // than a bare timestamp, so no incremental param.
                        params: { pageSize: 1000 },
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.SMARTSHEET]: {
        displayName: 'Smartsheet',
        sourceFields: [accessToken('Smartsheet API access token')],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.smartsheet.com/2.0/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // 1-based page number with a real page count in the body.
                paginator: {
                    type: 'page_number',
                    base_page: 1,
                    page_param: 'page',
                    total_path: 'totalPages',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'sheets',
                    endpoint: {
                        path: 'sheets',
                        // `modifiedSince` exists but applies to the sheet
                        // contents endpoint, not to this listing.
                        params: { pageSize: 100 },
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.ROLLBAR]: {
        displayName: 'Rollbar',
        sourceFields: [
            secretField('accessToken', 'Project read access token', 'Rollbar read access token'),
        ],
        oauth: null,
        // Error title: the only free text on an item. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'title',
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.rollbar.com/api/1/',
                auth: {
                    type: 'api_key',
                    name: 'X-Rollbar-Access-Token',
                    api_key: '${accessToken}',
                    location: 'header',
                },
                // 1-based page number with no total, so pagination stops on
                // the first empty page.
                paginator: {
                    type: 'page_number',
                    base_page: 1,
                    page_param: 'page',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'items',
                    endpoint: {
                        path: 'items',
                        // Every response wraps the payload in `result`.
                        data_selector: 'result.items',
                    },
                },
            ],
        }),
    },
    [ConnectorType.STATUSPAGE]: {
        displayName: 'Statuspage',
        sourceFields: [
            secretField('apiKey', 'API key', 'Statuspage API key'),
            textField('pageId', 'Page ID', 'Statuspage page id'),
        ],
        oauth: null,
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://api.statuspage.io/v1/',
                // The scheme is a literal `OAuth ` prefix, not Bearer.
                auth: {
                    type: 'api_key',
                    name: 'Authorization',
                    api_key: 'OAuth ${apiKey}',
                    location: 'header',
                },
                // 1-based page number with no total in the body.
                paginator: {
                    type: 'page_number',
                    base_page: 1,
                    page_param: 'page',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'incidents',
                    endpoint: {
                        // The response is a bare array, so no selector.
                        path: `pages/${requireString(source, 'pageId')}/incidents`,
                        params: { per_page: 100 },
                    },
                },
            ],
        }),
    },
    [ConnectorType.SURVEYMONKEY]: {
        displayName: 'SurveyMonkey',
        sourceFields: [
            accessToken('SurveyMonkey access token'),
            textField('surveyId', 'Survey ID', '123456789'),
        ],
        oauth: null,
        // The bulk endpoint nests answers three deep; choice-only answers carry
        // no `text` and are skipped.
        // Dotted/`[*]` path, the same notation as `data_selector` above: the
        // enrichment mapper runs at extract, so it sees the provider's raw
        // nested JSON, not dlt's flattened `source__body` column.
        feedbackTextColumn: 'pages[*].questions[*].answers[*].text',
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://api.surveymonkey.com/v3/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // `links.next` is an absolute URL, absent on the last page.
                paginator: {
                    type: 'json_link',
                    next_url_path: 'links.next',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'responses',
                    endpoint: {
                        // The bulk variant returns the answers, not just
                        // response metadata.
                        path: `surveys/${requireString(source, 'surveyId')}/responses/bulk`,
                        params: {
                            per_page: 100,
                            last_modified_at: {
                                type: 'incremental',
                                cursor_path: 'date_modified',
                                initial_value: '2020-01-01T00:00:00',
                            },
                        },
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.DELIGHTED]: {
        displayName: 'Delighted',
        sourceFields: [
            secretField('apiKey', 'API key', 'Delighted API key'),
        ],
        oauth: null,
        // The verbatim NPS comment. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'comment',
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.delighted.com/v1/',
                // API key as the Basic username, with an empty password.
                auth: {
                    type: 'http_basic',
                    username: '${apiKey}',
                    password: '',
                },
                // 1-based page number over a bare array, so pagination
                // stops on the first empty page.
                paginator: {
                    type: 'page_number',
                    base_page: 1,
                    page_param: 'page',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'survey_responses',
                    endpoint: {
                        // The response is a bare array, so no selector.
                        // `updated_since` is unix seconds, which no ISO
                        // incremental initial_value can seed.
                        path: 'survey_responses.json',
                        params: { per_page: 100 },
                    },
                },
            ],
        }),
    },
    [ConnectorType.CLOSE]: {
        displayName: 'Close',
        sourceFields: [secretField('apiKey', 'API key', 'api_...')],
        oauth: null,
        // Close's name for opportunity free text. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'note',
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.close.com/api/v1/',
                // API key as the Basic username, with an empty password.
                auth: {
                    type: 'http_basic',
                    username: '${apiKey}',
                    password: '',
                },
                // Underscore-prefixed skip/limit offset. The envelope's
                // `has_more` flag is not read: a missing has_more_path
                // raises, so stop on an empty page instead.
                paginator: {
                    type: 'offset',
                    limit: 100,
                    offset_param: '_skip',
                    limit_param: '_limit',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'opportunities',
                    endpoint: {
                        path: 'opportunity',
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.FRESHSALES]: {
        displayName: 'Freshsales',
        sourceFields: [
            secretField('apiKey', 'API key', 'Freshsales API key'),
            textField('bundleAlias', 'Bundle alias', 'acme'),
        ],
        oauth: null,
        buildSourceConfig: (source) => ({
            client: {
                base_url: `https://${requireString(source, 'bundleAlias')}.myfreshworks.com/crm/sales/api/`,
                // A literal `Token token=` prefix, as PagerDuty uses.
                auth: {
                    type: 'api_key',
                    name: 'Authorization',
                    api_key: 'Token token=${apiKey}',
                    location: 'header',
                },
                // 1-based page number with no total, so pagination stops on
                // the first empty page.
                paginator: {
                    type: 'page_number',
                    base_page: 1,
                    page_param: 'page',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'deals',
                    endpoint: {
                        // A view id scopes the listing; this filter form is
                        // the one part of this entry I am least sure of.
                        path: 'deals/view/0',
                        data_selector: 'deals',
                    },
                },
            ],
        }),
    },
    [ConnectorType.FRESHSERVICE]: {
        displayName: 'Freshservice',
        sourceFields: [
            secretField('apiKey', 'API key', 'Freshservice API key'),
            textField('subdomain', 'Subdomain', 'acme'),
        ],
        oauth: null,
        // Plain-text twin of the HTML description. Must be the provider's own spelling: a REST sync lands
        // it verbatim, unlike the CSV path which remaps synonyms.
        feedbackTextColumn: 'description_text',
        buildSourceConfig: (source) => ({
            client: {
                base_url: `https://${requireString(source, 'subdomain')}.freshservice.com/api/v2/`,
                // API key as the Basic username; the password is a literal X.
                auth: {
                    type: 'http_basic',
                    username: '${apiKey}',
                    password: 'X',
                },
                // 1-based page number. This API also emits Link headers,
                // but a page walk that stops on the first empty page is
                // correct either way.
                paginator: {
                    type: 'page_number',
                    base_page: 1,
                    page_param: 'page',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'tickets',
                    endpoint: {
                        path: 'tickets',
                        params: {
                            per_page: 100,
                            updated_since: {
                                type: 'incremental',
                                cursor_path: 'updated_at',
                                initial_value: '2020-01-01T00:00:00Z',
                            },
                        },
                        data_selector: 'tickets',
                    },
                },
            ],
        }),
    },
    [ConnectorType.AIRCALL]: {
        displayName: 'Aircall',
        sourceFields: [
            secretField('apiId', 'API ID', 'Aircall API id'),
            secretField('apiToken', 'API token', 'Aircall API token'),
        ],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.aircall.io/v1/',
                auth: {
                    type: 'http_basic',
                    username: '${apiId}',
                    password: '${apiToken}',
                },
                // `meta.next_page_link` is an absolute URL, null when done.
                paginator: {
                    type: 'json_link',
                    next_url_path: 'meta.next_page_link',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'calls',
                    endpoint: {
                        path: 'calls',
                        // `from`/`to` are unix seconds over creation time,
                        // which no ISO incremental initial_value can seed.
                        params: { per_page: 50 },
                        data_selector: 'calls',
                    },
                },
            ],
        }),
    },
    [ConnectorType.POSTMARK]: {
        displayName: 'Postmark',
        sourceFields: [
            secretField('serverToken', 'Server token', 'Postmark server token'),
        ],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.postmarkapp.com/',
                auth: {
                    type: 'api_key',
                    name: 'X-Postmark-Server-Token',
                    api_key: '${serverToken}',
                    location: 'header',
                },
                // count/offset with a real grand total in the envelope.
                paginator: {
                    type: 'offset',
                    limit: 500,
                    offset_param: 'offset',
                    limit_param: 'count',
                    total_path: 'TotalCount',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'MessageID',
            },
            resources: [
                {
                    name: 'outbound_messages',
                    endpoint: {
                        path: 'messages/outbound',
                        // A sent message is immutable, so there is nothing
                        // an updated-at cursor would pick up.
                        data_selector: 'Messages',
                    },
                },
            ],
        }),
    },
    [ConnectorType.BREVO]: {
        displayName: 'Brevo',
        sourceFields: [secretField('apiKey', 'API key', 'xkeysib-...')],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.brevo.com/v3/',
                auth: {
                    type: 'api_key',
                    name: 'api-key',
                    api_key: '${apiKey}',
                    location: 'header',
                },
                // limit/offset with a real grand total in the envelope.
                paginator: {
                    type: 'offset',
                    limit: 500,
                    offset_param: 'offset',
                    limit_param: 'limit',
                    total_path: 'count',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'contacts',
                    endpoint: {
                        path: 'contacts',
                        // `modifiedSince` exists, but sorting is by
                        // creation order, so a resumed cursor could skip
                        // rows — full refresh instead.
                        data_selector: 'contacts',
                    },
                },
            ],
        }),
    },
    [ConnectorType.OUTREACH]: {
        displayName: 'Outreach',
        sourceFields: [accessToken('Outreach OAuth access token')],
        // OAuth only: Outreach issues no static API key, so unlike its
        // neighbours in this section there is nothing else to collect.
        oauth: {
            tokenUrl: 'https://api.outreach.io/oauth/token',
            authorizeUrl: 'https://api.outreach.io/oauth/authorize',
            scopes: ['opportunities.read'],
            usePkce: false,
            authorizeParams: {},
            extraParams: {},
        },
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.outreach.io/api/v2/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // JSON:API: `links.next` is an absolute URL.
                paginator: {
                    type: 'json_link',
                    next_url_path: 'links.next',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'opportunities',
                    endpoint: {
                        path: 'opportunities',
                        // Incremental would need a `filter[updatedAt]`
                        // range expression, which no incremental param
                        // builds, so this is a full refresh.
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.SALESLOFT]: {
        displayName: 'Salesloft',
        sourceFields: [accessToken('Salesloft API key')],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.salesloft.com/v2/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // 1-based page number. The envelope reports paging under
                // `metadata.paging`, but no total is guaranteed, so
                // pagination stops on the first empty page.
                paginator: {
                    type: 'page_number',
                    base_page: 1,
                    page_param: 'page',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'people',
                    endpoint: {
                        path: 'people',
                        params: {
                            per_page: 100,
                            'updated_at[gte]': {
                                type: 'incremental',
                                cursor_path: 'updated_at',
                                initial_value: '2020-01-01T00:00:00Z',
                            },
                        },
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.ADYEN]: {
        displayName: 'Adyen',
        sourceFields: [
            secretField('apiKey', 'API key', 'AQEyhmfx...'),
            textField('balancePlatform', 'Balance platform', 'YourBalancePlatform'),
        ],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                // Transfers API. Adyen splits services across hosts and
                // only this one lists transactions; the checkout and pal
                // hosts additionally need a per-company live prefix.
                base_url: 'https://balanceplatform-api-live.adyen.com/btl/v4/',
                auth: {
                    type: 'api_key',
                    name: 'x-API-key',
                    api_key: '${apiKey}',
                    location: 'header',
                },
                // `_links.next.href` is an absolute URL, absent once the
                // last page is reached.
                paginator: {
                    type: 'json_link',
                    next_url_path: '_links.next.href',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'transactions',
                    endpoint: {
                        path: 'transactions',
                        params: {
                            balancePlatform: '${balancePlatform}',
                            limit: 100,
                            // Both bounds are required. Only a created-at
                            // filter exists, and a transaction's status
                            // flips pending -> booked after creation, so an
                            // incremental cursor here would freeze rows at
                            // their pending state: full refresh instead.
                            createdSince: '2020-01-01T00:00:00Z',
                            createdUntil: '2100-01-01T00:00:00Z',
                        },
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.LINKEDIN_ADS]: {
        displayName: 'LinkedIn Ads',
        sourceFields: [
            accessToken('LinkedIn access token'),
            textField('apiVersion', 'API version', '202401'),
        ],
        oauth: {
            tokenUrl: 'https://www.linkedin.com/oauth/v2/accessToken',
            authorizeUrl: 'https://www.linkedin.com/oauth/v2/authorization',
            scopes: ['r_ads'],
            // LinkedIn documents no PKCE support on this flow.
            usePkce: false,
            // Programmatic refresh is partner-gated rather than requested
            // at consent, so there is no offline-access param to send.
            authorizeParams: {},
            extraParams: {},
        },
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.linkedin.com/rest/',
                auth: { type: 'bearer', token: '${accessToken}' },
                headers: {
                    // Both are mandatory on versioned Marketing API calls.
                    'LinkedIn-Version': '${apiVersion}',
                    'X-Restli-Protocol-Version': '2.0.0',
                },
                // Cursor pagination from version 202401 onward. The docs
                // state pageToken is null once fewer than pageSize rows
                // come back.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'metadata.nextPageToken',
                    cursor_param: 'pageToken',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'ad_accounts',
                    endpoint: {
                        path: 'adAccounts',
                        params: {
                            // Rest.li finder. Listing requires it.
                            q: 'search',
                            pageSize: 1000,
                        },
                        // Records carry changeAuditStamps.lastModified but
                        // no request-side time filter exists to drive an
                        // incremental, so this is a full refresh.
                        data_selector: 'elements',
                    },
                },
            ],
        }),
    },
    [ConnectorType.SEGMENT]: {
        displayName: 'Segment',
        sourceFields: [accessToken('Segment API token')],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                // US workspaces. EU workspaces answer on eu1.
                base_url: 'https://api.segmentapis.com/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // Cursors are nested under `data`, and `next` is omitted
                // entirely once the last page is reached.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'data.pagination.next',
                    cursor_param: 'pagination.cursor',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'sources',
                    endpoint: {
                        path: 'sources',
                        params: { 'pagination.count': 200 },
                        // Source records carry no timestamp at all, so
                        // there is nothing to build an incremental on.
                        data_selector: 'data.sources',
                    },
                },
            ],
        }),
    },
    [ConnectorType.DOCUSIGN]: {
        displayName: 'Docusign',
        sourceFields: [
            accessToken('Docusign access token'),
            textField('accountBaseUri', 'Account base URI', 'na4'),
            textField('accountId', 'Account ID', '1234abcd-...'),
        ],
        oauth: {
            tokenUrl: 'https://account.docusign.com/oauth/token',
            authorizeUrl: 'https://account.docusign.com/oauth/auth',
            scopes: ['signature', 'extended'],
            usePkce: true,
            // Docusign returns a refresh token on the signature scope
            // without an extra consent param.
            authorizeParams: {},
            extraParams: {},
        },
        buildSourceConfig: (source) => ({
            client: {
                // Each account answers on its own regional host, returned
                // by the userinfo endpoint at authorization time.
                base_url: `https://${requireString(source, 'accountBaseUri')}.docusign.net/restapi/v2.1/accounts/${requireString(source, 'accountId')}/`,
                auth: { type: 'bearer', token: '${accessToken}' },
                // `nextUri` is a relative URI, documented as null on the
                // last page, and resolved against the request URL.
                paginator: {
                    type: 'json_link',
                    next_url_path: 'nextUri',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'envelopeId',
            },
            resources: [
                {
                    name: 'envelopes',
                    endpoint: {
                        path: 'envelopes',
                        params: {
                            count: 1000,
                            order_by: 'last_modified',
                            // Required, and a genuine status-change cursor
                            // rather than a created-at filter.
                            from_date: {
                                type: 'incremental',
                                cursor_path: 'statusChangedDateTime',
                                initial_value: '2020-01-01T00:00:00Z',
                            },
                        },
                        data_selector: 'envelopes',
                    },
                },
            ],
        }),
    },
    [ConnectorType.ZOOM]: {
        displayName: 'Zoom',
        sourceFields: [accessToken('Zoom access token')],
        oauth: {
            tokenUrl: 'https://zoom.us/oauth/token',
            authorizeUrl: 'https://zoom.us/oauth/authorize',
            scopes: ['user:read:admin'],
            usePkce: true,
            // Zoom's authorization code grant issues a refresh token by
            // default; no offline-access param exists.
            authorizeParams: {},
            extraParams: {},
        },
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.zoom.us/v2/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // Zoom does not document whether the token is empty or
                // absent when exhausted. It does not matter here: the
                // cursor paginator treats a falsy value as the end, so
                // "", null and absent all terminate identically.
                paginator: {
                    type: 'cursor',
                    cursor_path: 'next_page_token',
                    cursor_param: 'next_page_token',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'users',
                    endpoint: {
                        path: 'users',
                        // No updated-since filter exists on this endpoint,
                        // so this is a full refresh.
                        data_selector: 'users',
                    },
                },
            ],
        }),
    },
    [ConnectorType.DATADOG]: {
        displayName: 'Datadog',
        sourceFields: [
            secretField('apiKey', 'API key', 'Datadog API key'),
            secretField('appKey', 'Application key', 'Datadog app key'),
        ],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                // US site. Other regions answer on their own host.
                base_url: 'https://api.datadoghq.com/api/v2/',
                auth: {
                    type: 'api_key',
                    name: 'DD-API-KEY',
                    api_key: '${apiKey}',
                    location: 'header',
                },
                headers: {
                    // Reads need both keys, and dlt's auth carries one.
                    'DD-APPLICATION-KEY': '${appKey}',
                },
                // 0-based page number. The envelope reports only a total
                // count, so pagination stops on the first empty page.
                paginator: {
                    type: 'page_number',
                    base_page: 0,
                    page_param: 'page[number]',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'users',
                    endpoint: {
                        path: 'users',
                        params: { 'page[size]': 100 },
                        // Only a status filter exists, no time filter.
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.RIPPLING]: {
        displayName: 'Rippling',
        sourceFields: [accessToken('Rippling access token')],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                // v2. The legacy v1 host is api.rippling.com.
                base_url: 'https://rest.ripplingapis.com/',
                auth: { type: 'bearer', token: '${accessToken}' },
                headers: { Accept: 'application/json' },
                // `next_link` is an absolute URL, documented as null once
                // the last page is reached.
                paginator: {
                    type: 'json_link',
                    next_url_path: 'next_link',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'workers',
                    endpoint: {
                        path: 'workers/',
                        params: { limit: 100 },
                        // updated_at is filterable, but the filter
                        // expression grammar is not documented, so a full
                        // refresh beats guessing at one.
                        data_selector: 'results',
                    },
                },
            ],
        }),
    },
    [ConnectorType.PERSONIO]: {
        displayName: 'Personio',
        sourceFields: [
            secretField('clientId', 'Client ID', 'Personio client ID'),
            secretField('clientSecret', 'Client secret', 'Personio client secret'),
        ],
        oauth: null,
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.personio.de/v2/',
                // Client credentials, so dlt mints and renews the token
                // itself and no authorization-code redirect is needed.
                auth: {
                    type: 'oauth2_client_credentials',
                    access_token_url: 'https://api.personio.de/v2/auth/token',
                    client_id: '${clientId}',
                    client_secret: '${clientSecret}',
                },
                // `_meta.links.next.href` is an absolute URL carrying the
                // cursor, and the key is absent on the last page.
                paginator: {
                    type: 'json_link',
                    next_url_path: '_meta.links.next.href',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'persons',
                    endpoint: {
                        path: 'persons',
                        params: {
                            limit: 50,
                            'updated_at.gt': {
                                type: 'incremental',
                                cursor_path: 'updated_at',
                                initial_value: '2020-01-01T00:00:00Z',
                            },
                        },
                        data_selector: '_data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.DEEL]: {
        displayName: 'Deel',
        sourceFields: [accessToken('Deel API token')],
        oauth: {
            tokenUrl: 'https://app.deel.com/oauth2/tokens',
            authorizeUrl: 'https://app.deel.com/oauth2/authorize',
            scopes: ['people:read'],
            usePkce: false,
            authorizeParams: {},
            extraParams: {},
        },
        buildSourceConfig: () => ({
            client: {
                base_url: 'https://api.letsdeel.com/rest/',
                auth: { type: 'bearer', token: '${accessToken}' },
                // offset/limit against a total in the `page` envelope.
                paginator: {
                    type: 'offset',
                    limit: 100,
                    total_path: 'page.total_rows',
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'id',
            },
            resources: [
                {
                    name: 'people',
                    endpoint: {
                        path: 'people',
                        // No updated-at filter and no updated_at on the
                        // record, so this is a full refresh.
                        data_selector: 'data',
                    },
                },
            ],
        }),
    },
    [ConnectorType.GUSTO]: {
        displayName: 'Gusto',
        sourceFields: [
            accessToken('Gusto access token'),
            textField('companyId', 'Company ID', 'Gusto company UUID'),
        ],
        oauth: {
            tokenUrl: 'https://api.gusto.com/oauth/token',
            authorizeUrl: 'https://api.gusto.com/oauth/authorize',
            scopes: ['employees:read'],
            usePkce: true,
            authorizeParams: {},
            extraParams: {},
        },
        buildSourceConfig: (source) => ({
            client: {
                base_url: 'https://api.gusto.com/v1/',
                auth: { type: 'bearer', token: '${accessToken}' },
                headers: { 'X-Gusto-API-Version': '2026-06-15' },
                // Gusto reports paging in X-Total-Pages headers, which no
                // shipped paginator reads, so pagination stops on the
                // first empty page instead.
                paginator: {
                    type: 'page_number',
                    base_page: 1,
                    page_param: 'page',
                    total_path: null,
                },
            },
            resource_defaults: {
                write_disposition: 'merge',
                primary_key: 'uuid',
            },
            resources: [
                {
                    name: 'employees',
                    endpoint: {
                        path: `companies/${requireString(source, 'companyId')}/employees`,
                        params: { per: 100 },
                        // The response root is the array itself, and no
                        // updated-since filter exists.
                    },
                },
            ],
        }),
    },
};
export const CONNECTOR_TYPES = Object.values(ConnectorType);
/**
 * The one lookup into the manifest. `connector_type` is an unconstrained varchar
 * in the database, so a malformed row can carry a value outside the enum that
 * TypeScript believes is impossible: without this, the bare Record lookup
 * returns undefined and the caller dies on a property access instead.
 */
export const getConnectorManifest = (type) => {
    const entry = CONNECTOR_MANIFEST[type];
    if (entry === undefined) {
        throw new ParameterError(`Unknown connector type: ${String(type)}`);
    }
    return entry;
};
/** The label shown for a connector before it has a user-supplied name. */
export const defaultConnectorDisplayName = (type) => getConnectorManifest(type).displayName;
export const getConnectorSourceFields = (type) => getConnectorManifest(type).sourceFields;
/**
 * Per-provider token endpoint. `${key}` in `tokenUrl` is resolved against the
 * connector's own source credentials, the same convention the source configs
 * use, so a provider whose host is tenant-specific needs no extra code.
 */
export const CONNECTOR_OAUTH_CONFIG = Object.fromEntries(CONNECTOR_TYPES.map((type) => [type, CONNECTOR_MANIFEST[type].oauth]));
/** dlt `rest_api_source` config for one connector, from its manifest entry. */
export const buildSourceConfig = (connectorType, source) => getConnectorManifest(connectorType).buildSourceConfig(source);
//# sourceMappingURL=manifest.js.map